import z from 'zod/v4';
import { DefaultPresetSchema } from './default-preset-schema';
import { createMigration, createPresetSchema } from './utils';
import type { PresetConfig } from './types';
import {
  ApartmentContractConfig,
  BlockContractConfig,
  IndustrialPremisesContractConfig,
  OtherContractConfig,
  OutdoorSectionContractConfig,
  ParkingSpotContractConfig,
} from '@pigello/pigello-matrix';

const FormatInstructionSchema = z.object({
  datetime_format: z.string().optional(),
  date_format: z.string().optional(),
  float_decimal_amount: z.number().optional(),
  float_use_thousand_separator: z.boolean().optional(),
  float_use_dot_delimiter: z.boolean().optional(),
  include_address_country: z.boolean().optional(),
});

const OverrideFiueldValuesSchema = z.record(z.string(), z.string().nullable());

const PigelloEngine = z.literal('pigello');
const ScriveEngine = z.literal('scrive');

export const PRESET_FASTDOC_SIGNING_TYPE = {
  TERMINATION: 'termination',
  MOVE_IN: 'move-in',
} as const;

export const PRESET_FASTDOC_TENANT_TYPE = {
  PRIVATE: 'private',
  COMPANY: 'company',
} as const;

const V1DataSchema = z.object({
  title: z.string(),
  description: z.string().optional().nullable(),
  signingSettings: z.discriminatedUnion('engine', [
    z.object({
      engine: ScriveEngine,
      externalSignEngine: z.object({ id: z.string() }),
      daysToSign: z.number().optional().nullish(),
      daysToRemind: z.number().optional().nullish(),
      invitationMessage: z.string().optional().nullish(),
      confirmationMessage: z.string().optional().nullish(),
    }),
    z.object({
      engine: PigelloEngine,
    }),
  ]),

  fastdocTemplate: z.object({
    is_custom_template: z.boolean(),
    document_id: z.string().optional(),
    fastdok_template_id: z.number().optional(),
    template_identifier: z.string().nullable().optional(),

    overriden_field_values: z.optional(OverrideFiueldValuesSchema),

    format_instruction: z.optional(FormatInstructionSchema),

    direct_attachment_cases: z
      .array(
        z.object({
          fastdok_template_id: z.number(),
          template_identifier: z.string().nullable(),
          overriden_field_values: OverrideFiueldValuesSchema,
        })
      )
      .optional(),

    attachment_cases: z
      .array(
        z.object({
          document_id: z.string().optional(),
          fastdok_template_id: z.number(),
          template_identifier: z.string().nullable(),
          overriden_field_values: OverrideFiueldValuesSchema,
        })
      )
      .optional(),
  }),
});

export const V1Schema = createPresetSchema('1', V1DataSchema);

const V1_0_1DataSchema = z.object({
  ...V1DataSchema.shape,

  // Null means that all contract types are allowed
  // Empty array means no contract type is allowed
  restrictedToContractTypes: z
    .array(
      z.literal([
        IndustrialPremisesContractConfig.modelName,
        ApartmentContractConfig.modelName,
        OutdoorSectionContractConfig.modelName,
        ParkingSpotContractConfig.modelName,
        OtherContractConfig.modelName,
        BlockContractConfig.modelName,
      ])
    )
    .nullable(),

  restrictedToSigningType: z
    .union([
      z.literal(PRESET_FASTDOC_SIGNING_TYPE.MOVE_IN),
      z.literal(PRESET_FASTDOC_SIGNING_TYPE.TERMINATION),
    ])
    .nullable(),

  restrictedToTenantType: z
    .union([
      z.literal(PRESET_FASTDOC_TENANT_TYPE.PRIVATE),
      z.literal(PRESET_FASTDOC_TENANT_TYPE.COMPANY),
    ])
    .nullable(),

  tags: z.array(z.string()),
});

export const V1_0_1_Schema = createPresetSchema('1.0.1', V1_0_1DataSchema);

const V1ToV1_0_1 = createMigration(V1Schema, V1_0_1_Schema, {
  fromVersion: '1',
  migrator: (data) => {
    return {
      ...data,
      version: '1.0.1' as const,
      presetData: {
        ...data.presetData,
        restrictedToContractTypes: null,
        restrictedToSigningType: null,
        restrictedToTenantType: null,
        tags: [],
      },
    };
  },
});

export const FASTDOC_TEMPLACE_PRESET_IDENTIFIER = 'fastdoc.template';

const FastdocTemplatePresetSchema = z.object({
  ...DefaultPresetSchema.shape,
  context: z.literal(FASTDOC_TEMPLACE_PRESET_IDENTIFIER),
  data: V1_0_1_Schema,
});

export const FastDocTemplatePreset = {
  identifier: FASTDOC_TEMPLACE_PRESET_IDENTIFIER,
  currentSchema: FastdocTemplatePresetSchema,
  currentVersion: '1.0.1' as const,
  migrations: [V1ToV1_0_1],
  single: false,
} as const satisfies PresetConfig;

export type FastdocTemplatePreset = z.infer<typeof FastdocTemplatePresetSchema>;
export type FastdocTemplatePresetData =
  FastdocTemplatePreset['data']['presetData'];
